import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { FiBook, FiPlus, FiToggleLeft, FiToggleRight, FiEye, FiTrash2, FiCopy, FiEdit2, FiCheckCircle } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const Quizzes = () => {
  const { isAdmin, isStudent } = useAuth();
  const navigate = useNavigate();

  const { data: quizzes, isLoading, refetch } = useQuery({
    queryKey: ['quizzes'],
    queryFn: () => axios.get('/api/quizzes').then(res => res.data.quizzes)
  });

  const toggleQuizStatus = async (quizId, currentStatus) => {
    try {
      await axios.put(`/api/quizzes/${quizId}/status`, {
        isActive: !currentStatus
      });
      toast.success(`Quiz ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      refetch();
    } catch (error) {
      toast.error('Failed to update quiz status');
    }
  };

  const handleDeleteQuiz = async (quizId, quizTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${quizTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/api/quizzes/${quizId}`);
      toast.success('Quiz deleted successfully');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete quiz');
    }
  };

  const handleDuplicateQuiz = async (quizId) => {
    try {
      await axios.post(`/api/quizzes/${quizId}/duplicate`);
      toast.success('Quiz duplicated successfully');
      refetch();
    } catch (error) {
      toast.error('Failed to duplicate quiz');
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          {isAdmin ? 'My Quizzes' : 'Available Quizzes'}
        </h1>
        {isAdmin && (
          <Link to="/quizzes/create" className="btn-primary">
            <FiPlus className="inline mr-2" />
            Create Quiz
          </Link>
        )}
      </div>

      {isStudent && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            ℹ️ These quizzes are available in your enrolled classrooms. Access them through your classroom to attempt.
          </p>
        </div>
      )}

      {quizzes?.length === 0 ? (
        <div className="card text-center py-12">
          <FiBook className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-lg text-gray-600">
            {isAdmin 
              ? "You haven't created any quizzes yet"
              : "No quizzes available in your classrooms"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quizzes.map((quiz) => (
            <div
              key={quiz._id}
              className="card hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {quiz.title}
                </h3>
                <div className="flex items-center space-x-2">
                  {quiz.isActive ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                      Inactive
                    </span>
                  )}
                  {isStudent && quiz.hasAttempted && (
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      <FiCheckCircle className="inline mr-1" size={12} />
                      Attempted
                    </span>
                  )}
                </div>
              </div>

              {quiz.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{quiz.description}</p>
              )}

              <div className="text-sm text-gray-500 space-y-1">
                <p>{quiz.questions?.length || 0} questions</p>
                <p>{quiz.duration} minutes</p>
                <p>{quiz.totalPoints} points</p>
                {isAdmin && (
                  <p>Created by: {quiz.createdBy?.name || 'You'}</p>
                )}
              </div>

              <div className="mt-4">
                {isAdmin ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <Link
                        to={`/quizzes/${quiz._id}`}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                      >
                        <FiEye className="inline mr-1" />
                        View Details
                      </Link>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/quizzes/${quiz._id}/edit`)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Edit quiz"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDuplicateQuiz(quiz._id)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Duplicate quiz"
                        >
                          <FiCopy size={16} />
                        </button>
                        <button
                          onClick={() => toggleQuizStatus(quiz._id, quiz.isActive)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          {quiz.isActive ? (
                            <FiToggleRight size={20} className="text-green-600" />
                          ) : (
                            <FiToggleLeft size={20} />
                          )}
                        </button>
                      </div>
                      <button
                        onClick={() => handleDeleteQuiz(quiz._id, quiz.title)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="pt-3 border-t border-gray-200">
                    {quiz.hasAttempted ? (
                      <Link
                        to={`/quizzes/${quiz._id}/view-result`}
                        className="btn-primary text-sm w-full text-center"
                      >
                        <FiEye className="inline mr-1" />
                        View My Answers
                      </Link>
                    ) : (
                      <p className="text-sm text-gray-500 text-center italic">
                        Go to classroom to attempt
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Quizzes;